import * as React from 'react';
import Head from 'next/head'
import { useState, useEffect } from 'react';
import type { AppProps } from 'next/app'
import Script from 'next/script';

import { Provider } from 'react-redux';
import 'core-js/stable';
import 'src/utils/index.css';
import 'regenerator-runtime/runtime';
import { I18nextProvider } from 'react-i18next';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';

import { initMockAzarJs } from 'src/utils/init';
import { initializeI18next } from 'src/utils/i18next';
import { Spinner } from 'src/component/Spinner';
import configureStore from 'src/store';
import { Store } from 'src/store/types';
import { QueryClient, QueryClientProvider } from 'react-query';


const App: React.FC<AppProps> = ({ Component, pageProps }) => {
  const [queryClient] = useState(new QueryClient());
  const [isLoaded, setIsLoaded] = useState<boolean>(typeof window !== 'undefined' && !!window.AzarJs);
  const [store, setStore] = useState<Store | null>(null);
  const [i18n, setI18n] = useState<any | null>(null);
  const [isRtl, setIsRtl] = useState<boolean>(false);

  useEffect(() => {
    let timer: number;
    if (isLoaded) {
      const nextStore = configureStore();
      setStore(nextStore);
      initializeI18next(nextStore.getState().locale.language).then(setI18n);
      setIsRtl(nextStore.getState().locale.isRtl);
    } else {
      let count = 0;
      timer = window.setInterval(() => {
        const nextIsLoaded = !!window.AzarJs;
        count += 1;
        if (nextIsLoaded || count > 15 || process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
          if (!nextIsLoaded) {
            // initMockAzarJs(initMockProduct);
            initMockAzarJs(true);
          }
          clearInterval(timer);
          timer = window.setTimeout(() => {
            setIsLoaded(true);
          }, 1000);
        }
      }, 200);
    }
    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  return (
    <QueryClientProvider client={queryClient}>
      {!(isLoaded && store && i18n) ? (
            <Overlay>
              <Spinner />
            </Overlay>
            ) : (
      <Provider store={store}>
          <Sentry.ErrorBoundary>
            <Head>
              <meta
                name='viewport'
                content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
              />
            </Head>
            <Script
              id='ga'
              dangerouslySetInnerHTML={{
                __html: `
                  (function(w, d, s, l, i) {
                      w[l] = w[l] || [];
                      w[l].push({
                          "gtm.start": new Date().getTime(),
                          event: "gtm.js"
                      });
                      var f = d.getElementsByTagName(s)[0],
                          j = d.createElement(s),
                          dl = l != "dataLayer" ? "&l=" + l : "";
                      j.async = true;
                      j.src =
                          "https://www.googletagmanager.com/gtm.js?id=" +
                          i +
                          dl +
                          "&gtm_auth=HasB8nFIjf6oL5S5gwSWtw&gtm_preview=env-6&gtm_cookies_win=x";
                      f.parentNode.insertBefore(j, f);
                  })(window, document, "script", "dataLayer", "GTM-MF3H5MP");
                `
              }}
            />
            <I18nextProvider i18n={i18n}>
              <Root dir={isRtl ? 'rtl' : 'ltr'}>
                <Component {...pageProps} />
                <div id="modal-root"></div>
              </Root>
            </I18nextProvider>
          </Sentry.ErrorBoundary>
      </Provider>
      )}
    </QueryClientProvider>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Overlay = styled(Root)`
  background-color: rgba(0,0,0, .3);
  position: absolute;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export default App;
